<template>
  <div class="mt-3">
    <b-row>
      <b-col md="12" sm="12">
        <p class="mb-4 px-4">
          <small>
            Quand vous attribuez une rétroaction à une intervention, le système
            inscrit automatiquement des notes dans la grille d'évaluation. Vous
            pouvez modifier la rétroaction ou les notes en tout temps.
          </small>
        </p>
        <hr />
      </b-col>
    </b-row>
    <template v-if="callGrids !== 'fetching'">
      <div v-for="grid in grids.data.data" :key="grid.id">
        <b-row
          class="pt-3 dlibr-collapse-unit toggle-trigger"
          v-b-toggle="`toggle-${grid.id}`"
        >
          <b-col>
            <b-icon
              class="mr-2"
              icon="chevron-right"
              variant="primary"
            ></b-icon>
            <strong>
              {{ grid.name }}
            </strong>
            <small v-if="grid.groups.length">
              - Groupe(s) assigné(s):
              {{ grid.groups.map(g => g.name).join(", ") }}</small
            >
          </b-col>
        </b-row>
        <b-collapse
          :id="`toggle-${grid.id}`"
          :visible="grids.data.data.length <= 1"
          accordion="my-accordion"
          role="tabpanel"
        >
          <section class="pt-3">
            <div class="container-fluid">
              <p class="text-info m-0">
                <strong>Critères d'évaluation</strong>
              </p>
              <div class="text-right mb-1">
                <b-icon
                  class="mr-2"
                  variant="secondary"
                  icon="info-circle-fill"
                ></b-icon>

                <small>
                  Cliquez directement sur la case pour faire vos changements.
                </small>
              </div>
              <b-table
                :fields="fields"
                :items="grid.comment_criteria"
                stacked="md"
                small
              >
                <template v-slot:thead-top>
                  <b-tr>
                    <b-th></b-th>
                    <b-th></b-th>
                    <b-th colspan="6" variant="secondary" class="text-center"
                      >Pointage par critère attribué selon la note</b-th
                    >
                  </b-tr>
                </template>
                <template v-slot:cell(shortcut)="row">
                  <b-form-input
                    :ref="`shortcut-${row.item.id}`"
                    class="grid-input"
                    type="text"
                    :value="row.item.shortcut"
                    :state="validMeta[row.field.key][row.item.id]"
                    @change="commentGradeChange(row, $event)"
                  ></b-form-input>
                </template>
                <template v-slot:cell(comment)="row">
                  <b-form-input
                    :ref="`comment-${row.item.id}`"
                    class="grid-input"
                    type="text"
                    :value="row.item.comment"
                    :state="validMeta[row.field.key][row.item.id]"
                    @change="commentGradeChange(row, $event)"
                  ></b-form-input>
                </template>
                <template v-slot:cell(clarity)="row">
                  <b-form-input
                    :ref="getGridValue(row, row.field.id).id"
                    class="text-center grid-input"
                    type="number"
                    min="0"
                    max="100"
                    :state="valid[getGridValue(row, row.field.id).id]"
                    :value="getGridValue(row, row.field.id).points"
                    @change="onChange(row, $event)"
                  ></b-form-input>
                </template>
                <template v-slot:cell(coherence)="row">
                  <b-form-input
                    :ref="getGridValue(row, row.field.id).id"
                    class="text-center grid-input"
                    type="number"
                    min="0"
                    max="100"
                    :state="valid[getGridValue(row, row.field.id).id]"
                    :value="getGridValue(row, row.field.id).points"
                    @change="onChange(row, $event)"
                  ></b-form-input>
                </template>
                <template v-slot:cell(pertinence)="row">
                  <b-form-input
                    :ref="getGridValue(row, row.field.id).id"
                    class="text-center grid-input"
                    type="number"
                    min="0"
                    max="100"
                    :state="valid[getGridValue(row, row.field.id).id]"
                    :value="getGridValue(row, row.field.id).points"
                    @change="onChange(row, $event)"
                  ></b-form-input>
                </template>
                <template v-slot:cell(richesse)="row">
                  <b-form-input
                    :ref="getGridValue(row, row.field.id).id"
                    class="text-center grid-input"
                    type="number"
                    min="0"
                    max="100"
                    :state="valid[getGridValue(row, row.field.id).id]"
                    :value="getGridValue(row, row.field.id).points"
                    @change="onChange(row, $event)"
                  ></b-form-input>
                </template>
              </b-table>
              <b-btn @click="openGridModal(grid)" pill variant="outline-info"
                >Ajouter une rétroaction</b-btn
              >
            </div>
          </section>
          <div class="container-fluid">
            <GridReference :grid="grid" />
          </div>
        </b-collapse>
      </div>
    </template>
    <template v-else>
      <b-card class="message">
        <div class="text-center">
          <b-spinner></b-spinner>
          <p>Actualisation en cours...</p>
        </div>
      </b-card>
    </template>
    <!-- User Interface controls -->
    <b-row>
      <b-col cols="4" class="my-5">
        <b-pagination
          pills
          v-model="table.currentPage"
          :total-rows="table.totalRows"
          :per-page="table.perPage"
          class="my-0"
          @change="pageChange"
        ></b-pagination>
      </b-col>
    </b-row>
    <GridCommentModal />
  </div>
</template>

<script>
/**
 * @file       Comment grid component.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import Vue from "vue";
import { mapState } from "vuex";
import { debounce } from "lodash";
import GridReference from "@/modules/grid/components/reference.vue";
import GridCommentModal from "@/modules/grid/components/gridCommentModal.vue";

/**
 * Export the component.
 * 
 * @type  {Object}
 */
export default {
  name: "gridComment",
  components: { GridCommentModal, GridReference },

  /**
   * Data used in the component.
   *
   * @return  {Object}
   */
  data() {
    return {
      fields: [
        {
          key: "shortcut",
          label: "Votre raccourci"
        },
        {
          key: "comment",
          label: "Rétroaction montrée au participant"
        },
        {
          key: "clarity",
          label: "Clarté %",
          class: "text-center",
          concept: "Clarté",
          name: "points"
        },
        {
          key: "coherence",
          label: "Cohérence %",
          class: "text-center",
          concept: "Cohérence",
          name: "points"
        },
        {
          key: "pertinence",
          label: "Pertinence %",
          class: "text-center",
          concept: "Pertinence",
          name: "points"
        },
        {
          key: "richesse",
          label: "Richesse %",
          class: "text-center",
          concept: "Richesse",
          name: "points"
        }
      ],
      valid: {},
      validMeta: { shortcut: {}, comment: {} },
      validComment: {}
    };
  },

  /**
   * Computed properties.
   * 
   * @type  {Object}
   */
  computed: {
    ...mapState({
      grids: state => state.grid.comment,
      callGrids: state => state.grid.comment.status
    }),

    /**
     * Pager table.
     *
     * @return     {Object}
     */
    table() {
      return {
        totalRows: this.grids.data.total,
        currentPage: this.grids.data.current_page,
        perPage: this.grids.data.per_page
      };
    }
  },

  /**
   * Methods.
   * 
   * @type  {Object}
   */
  methods: {
    /**
     * Change grid comment page.
     *
     * @param      {int}  page     The page number
     */
    pageChange(page) {
      this.$store.dispatch("grid/getGrid", { page, resourcesPerPage: 10 });
    },

    /**
     * Open grid comment modal.
     *
     * @param      {Object}  grid     The grid
     */
    openGridModal(grid) {
      this.$store.commit("grid/setCurrentGrid", grid);
      this.$bvModal.show("gridCommentModal");
    },

    /**
     * Get grid value.
     *
     * @param      {Object}  row     The row
     */
    getGridValue(row) {
      let found = null;
      row.item.comment_criteria_has_grid.forEach(c => {
        c.evaluation_concept.forEach(e => {
          if (e.concept == row.field.concept) {
            found = c;
          }
        });
      });

      return found || row.item;
    },

    /**
     * Change grid comment.
     *
     * @param      {Object}  row     The row
     * @param      {string}  value   The value
     */
    onChange: debounce(function(row, value) {
      const item = this.getGridValue(row);
      item.comment_criteria
        ? this.update(row, item, value)
        : this.create(row, item, value);
    }, 350),

    /**
     * Change comment grade.
     *
     * @param      {Object}  row     The row
     * @param      {string}  value   The value
     */
    commentGradeChange: debounce(function(row, value) {
      this.updateGradeComment(row, value);
    }, 350),

    /**
     * Update the grid comment.
     *
     * @param      {Object}  row     The row
     * @param      {Object}  item    The item
     * @param      {string}  value   The value
     */
    update(row, item, value) {
      const payload = {};
      payload.id = item.id;
      payload[row.field.name] = value;

      Vue.set(this.valid, item.id, false);

      this.$store
        .dispatch("grid/putGridComment", payload)
        .then(() => {
          Vue.set(this.valid, item.id, null);
          this.$root.$bvToast.toast("Mise à jour effectuée avec succès.", {
            title: "Succès"
          });
        })
        .catch(() => {
          Vue.set(this.valid, item.id, null);
          this.$nextTick(() => {
            this.$refs[this.getGridValue(row).id][0].$el.value =
              item[row.field.name];
          });
          this.$root.$bvToast.toast("La mise à jour à échouée", {
            title: "Erreur",
            variant: "danger"
          });
        });
    },

    /**
     * Create the grid comment criteria.
     *
     * @param      {Object}  row     The row
     * @param      {Object}  item    The item
     * @param      {string}  value   The value
     */
    create(row, item, value) {
      const grid = this.grids.data.data.find(g => g.id == row.item.grid);
      const evaluation_concept = grid.evaluation_concepts.find(
        e => e.concept == row.field.concept
      ).id;

      const payload = {
        comment_criteria: item.id,
        grid: row.item.grid,
        evaluation_concept
      };

      payload[row.field.name] = value;

      this.$store
        .dispatch("grid/postGridCommentCriteria", payload)
        .then(() => {
          this.$root.$bvToast.toast("Mise à jour effectuée avec succès.", {
            title: "Succès"
          });
        })
        .catch(() => {
          Vue.set(this.valid, item.id, null);
          this.$nextTick(() => {
            this.$refs[this.getGridValue(row).id][0].$el.value =
              item[row.field.name];
          });
          this.$root.$bvToast.toast("La mise à jour à échouée", {
            title: "Erreur",
            variant: "danger"
          });
        });
    },

    /**
     * Update the grade comment.
     *
     * @param      {Object}  row     The row
     * @param      {string}  value   The value
     */
    updateGradeComment(row, value) {
      const payload = {
        grid: row.item.grid,
        comment_criteria_grid: row.item.id
      };
      payload[row.field.key] = value;

      Vue.set(this.validMeta[row.field.key], row.item.id, false);

      this.$store
        .dispatch("grid/putCommentGrade", payload)
        .then(() => {
          Vue.set(this.validMeta[row.field.key], row.item.id, null);
          this.$root.$bvToast.toast("Mise à jour effectuée avec succès.", {
            title: "Succès"
          });
        })
        .catch(err => {
          Vue.set(this.validMeta[row.field.key], row.item.id, null);
          this.$nextTick(() => {
            this.$refs[`${row.field.key}-${row.item.id}`][0].$el.value =
              row.item[row.field.key];
          });
          const errorMessage =
            err.response.data.message == "The shorcut is already on this grid"
              ? "Le raccourci est déjà utilisé, veuillez en choisir un autre."
              : err.response.data.message;

          this.$root.$bvToast.toast(errorMessage, {
            title: "Erreur",
            variant: "danger"
          });
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.grid-input {
  border: none;
  box-shadow: none;
  padding-right: 0;
  padding-left: 0;
}
.toggle-trigger[aria-expanded="true"] .bi-chevron-right {
  transform: rotate(90deg);
}
.toggle-trigger .bi-chevron-right {
  transition: 0.3s transform ease-in-out;
}
.main-content {
  border-right: 1px solid rgba(0, 0, 0, 0.1);
}
.body-profile {
  margin-top: 1%;
}
</style>
